import { memo, FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRelative } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Lock, FileUp } from 'lucide-react';
import dateFnsLocales from "@/utils/dateFnsLocales";
import useTextDecryption from '@/hooks/useTextDecryption';
import { useAuth } from '@/contexts/AuthContext';
import { Message as MessageType } from '@/utils/api/aboboApiTypes';
import { cn } from '@/lib/utils';
import { sanitizeUrl } from '@braintree/sanitize-url';

/**
 * Extracts the domain from a URL for display purposes.
 * Returns the hostname without 'www.' prefix.
 */
function getDisplayDomain(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/**
 * Checks if the link text is a raw URL (auto-linked) vs explicit markdown link text.
 * If it's a raw URL, we abbreviate it to just the domain.
 */
function getAbbreviatedLinkText(href: string, children: ReactNode): ReactNode {
  // Get the text content from children
  const childText = typeof children === 'string'
    ? children
    : Array.isArray(children) && typeof children[0] === 'string'
      ? children[0]
      : null;

  // If the link text looks like the href (auto-linked URL), abbreviate to domain
  if (childText && (childText === href || childText.startsWith('http'))) {
    const domain = getDisplayDomain(href);
    if (domain) {
      return domain;
    }
  }

  // Otherwise return original children (explicit markdown link text)
  return children;
}

interface MessageProps {
  message: MessageType;
}

const MessageComponent: FC<MessageProps> = ({ message }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  // If user reset their password, messages sent before the reset cannot be decrypted.
  // Skip decryption entirely for these messages.
  const isBeforePasswordReset = useMemo(() => {
    if (!user?.resetRequestedAt || !message?.createdAt) return false;
    const resetDate = new Date(user.resetRequestedAt);
    const messageDate = new Date(message.createdAt);
    return messageDate < resetDate;
  }, [user?.resetRequestedAt, message?.createdAt]);

  // Only attempt decryption if message is after password reset (or no reset occurred)
  const { decryptedText, isProcessing, isSuccess } = useTextDecryption(
    isBeforePasswordReset ? '' : message?.body,
    isBeforePasswordReset ? '' : message?.patientAccessKey
  );

  const isOutgoing = message.direction === 'outgoing';

  const renderMessageContent = () => {
    // Message from before password reset - expected to be unavailable
    if (isBeforePasswordReset) {
      return <span className="italic">{t('secure.messaging.errors.messageUnavailable')}</span>;
    }
    if (isProcessing) {
      return <span className="italic">{t('secure.messaging.tooltips.decryptingMessage')}</span>;
    }
    if (isSuccess && decryptedText) {
      return (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Paragraphs
            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
            // Links - sanitized and abbreviated for long URLs
            a: ({ href, children }) => (
              <a
                href={sanitizeUrl(href ?? '')}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:opacity-80"
                title={href}
              >
                {getAbbreviatedLinkText(href ?? '', children)}
              </a>
            ),
            // Headings - prominent styling for chat context
            h1: ({ children }) => (
              <h1 className="text-2xl font-bold mt-3 mb-2 first:mt-0">
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2 className="text-xl font-semibold mt-3 mb-2 first:mt-0">
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 className="text-lg font-semibold mt-2 mb-1 first:mt-0">
                {children}
              </h3>
            ),
            // Bold and italic
            strong: ({ children }) => (
              <strong className="font-bold">{children}</strong>
            ),
            em: ({ children }) => <em className="italic">{children}</em>,
            // Code blocks
            pre: ({ children }) => (
              <pre className={cn(
                "rounded p-2 font-mono text-sm overflow-x-auto my-2",
                isOutgoing ? "bg-black/10" : "bg-white/10"
              )}>
                {children}
              </pre>
            ),
            code: ({ children }) => (
              <code className={cn(
                "rounded px-1 py-0.5 font-mono text-sm",
                isOutgoing ? "bg-black/10" : "bg-white/10"
              )}>
                {children}
              </code>
            ),
            // Blockquotes
            blockquote: ({ children }) => (
              <blockquote className="border-l-2 border-current/30 pl-3 italic my-2">
                {children}
              </blockquote>
            ),
            // Horizontal rule
            hr: () => <hr className="border-t border-current/30 my-3" />,
            // Lists
            ul: ({ children }) => (
              <ul className="list-disc pl-5 my-2">{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className="list-decimal pl-5 my-2">{children}</ol>
            ),
            li: ({ children }) => <li className="my-0.5">{children}</li>,
          }}
        >
          {decryptedText}
        </ReactMarkdown>
      );
    }

    return <span className="italic">{t('secure.messaging.errors.decryptionFailed')}</span>;
  }

  return (
    <div
      className={cn(
        'flex flex-col px-4 py-3 max-w-[90%] sm:max-w-[75%] rounded-t-lg shadow-lg',
        message.direction === 'outgoing' ? 'self-start rounded-br-lg bg-primary/40' : 'self-end rounded-bl-lg bg-primary text-primary-foreground'
      )}
    >
      {/* Message Body */}
      <div className="text-lg text-left max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
        {renderMessageContent()}
      </div>
      {/* Attachment indicator */}
      {message.direction === 'incoming' && message.hasAttachment && (
        <div className="flex justify-start">
          <div className="flex items-center gap-1 bg-white px-2 rounded-full text-sm text-gray-500">
            <FileUp className="size-3" />
            <span>{t('secure.messaging.titles.fileAttached')}</span>
          </div>
        </div>
      )}
      {/* Message metadata */}
      <div className={cn(
        'flex items-center gap-2 text-gray-600 justify-end',
        message.direction === 'incoming' ? 'justify-end text-primary-foreground/70' : ''
      )}>
        <span className="text-sm opacity-90">
          {formatRelative(new Date(message?.createdAt), new Date(), { 
            locale: dateFnsLocales[i18n.language]
          })}
        </span>
        <div className="flex items-center justify-center" 
             title={t('secure.messaging.tooltips.secureMessage')}>
          <Lock className="size-3" />
        </div>
      </div>
    </div>
  );
};

// Memoization comparison function to prevent unnecessary re-renders
const areEqual = (prevProps: MessageProps, nextProps: MessageProps) => {
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.body === nextProps.message.body &&
    prevProps.message.patientAccessKey === nextProps.message.patientAccessKey &&
    prevProps.message.direction === nextProps.message.direction &&
    prevProps.message.hasAttachment === nextProps.message.hasAttachment &&
    prevProps.message.createdAt === nextProps.message.createdAt &&
    prevProps.message.updatedAt === nextProps.message.updatedAt
  );
};

const Message = memo(MessageComponent, areEqual);
Message.displayName = 'Message';

export default Message;
